import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FautrasLogo } from "@/components/FautrasLogo";
import { LogOut, KeyRound, Calculator, Eye, Inbox } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LeadStatutSelect } from "@/components/LeadStatut";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROJET = { van: "Van tracté", fourgon: "Remorque fourgon" };
const ACHAT = { immediat: "Immédiat", moins_3_mois: "< 3 mois", moins_1_an: "< 1 an", plus_1_an: "> 1 an" };
const REPRISE = { non: "Non", van_tracte: "Van tracté", camion: "Camion chevaux" };
const PERMIS = { b: "Permis B", b96: "B96", be: "Permis BE" };

export default function MesClients() {
  const { user, loading, logout, authHeader } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [params] = useSearchParams();
  const leadParam = params.get("lead");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/vendeur/leads`, { headers: authHeader() });
      setLeads(data);
      // Lien « Voir la simulation du client » reçu par e-mail : ouverture directe.
      if (leadParam) {
        const lead = data.find((l) => l.id === leadParam);
        if (lead) openSimulation(lead);
        else toast.error("Ce client n'est pas rattaché à votre compte.");
      }
    } catch { toast.error("Erreur de chargement."); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeader, leadParam]);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate(`/login?next=${encodeURIComponent(`/mes-clients${leadParam ? `?lead=${leadParam}` : ""}`)}`); return; }
    if (user.must_change_password) { navigate("/compte"); return; }
    load();
  }, [user, loading, navigate, load, leadParam]);

  const openSimulation = (lead) => {
    const answers = lead?.simulation?.answers;
    if (!answers) { toast.error("Simulation non disponible pour ce client."); return; }
    sessionStorage.setItem("fautras_prefill", JSON.stringify(answers));
    navigate("/");
  };

  if (loading || !user) return <div className="min-h-screen bg-[#faf7f1]" />;

  return (
    <div className="min-h-screen bg-[#faf7f1]">
      <header className="bg-[#434343] text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <FautrasLogo dark />
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/70 hidden sm:inline">{user.email}</span>
            <Button size="sm" variant="secondary" onClick={() => navigate("/")} data-testid="mesclients-simulateur" className="rounded-full">
              <Calculator className="h-4 w-4 mr-1.5" /> Simulateur
            </Button>
            <Button size="sm" variant="secondary" onClick={() => navigate("/compte")} data-testid="mesclients-compte" className="rounded-full">
              <KeyRound className="h-4 w-4 mr-1.5" /> Mon compte
            </Button>
            <Button size="sm" variant="secondary" onClick={() => { logout(); navigate("/login"); }} data-testid="mesclients-logout" className="rounded-full">
              <LogOut className="h-4 w-4 mr-1.5" /> Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="font-heading text-2xl font-extrabold text-[#2b2b2b] mb-1 flex items-center gap-2">
          <Inbox className="h-6 w-6 text-[#9E7B41]" /> Mes clients
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          Les prospects de votre secteur ({leads.length}). Cliquez sur « Voir la simulation » pour rouvrir le parcours du client.
        </p>

        <div className="bg-white rounded-2xl border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="mesclients-table">
            <thead className="bg-[#faf7f1] text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Date</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Contact</th><th className="px-4 py-3">CP / Ville</th>
                <th className="px-4 py-3">Projet</th><th className="px-4 py-3">Échéance</th>
                <th className="px-4 py-3">Qualification</th><th className="px-4 py-3">Simulation</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-border align-top">
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{(l.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <LeadStatutSelect lead={l} authHeader={authHeader}
                      onChanged={(id, statut) => setLeads((p) => p.map((x) => (x.id === id ? { ...x, statut } : x)))} />
                  </td>
                  <td className="px-4 py-3 font-semibold text-[#2b2b2b]">{l.prenom} {l.nom}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    <a href={`mailto:${l.email}`} className="hover:text-[#9E7B41]">{l.email}</a><br />
                    <a href={`tel:${l.tel}`} className="hover:text-[#9E7B41]">{l.tel}</a>
                  </td>
                  <td className="px-4 py-3">{l.code_postal} {l.ville}</td>
                  <td className="px-4 py-3">{PROJET[l.projet] || l.projet}</td>
                  <td className="px-4 py-3">{ACHAT[l.projet_achat] || l.projet_achat}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {l.van_reprendre ? <>Reprise : {REPRISE[l.van_reprendre] || l.van_reprendre}<br /></> : null}
                    {l.nb_chevaux ? <>{l.nb_chevaux} cheval(aux)<br /></> : null}
                    {l.permis ? <>{PERMIS[l.permis] || l.permis}</> : null}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#2b2b2b] max-w-[200px]">{l.simulation?.resume || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => openSimulation(l)}
                      data-testid={`mesclients-open-${l.id}`} className="rounded-full whitespace-nowrap">
                      <Eye className="h-4 w-4 mr-1.5" /> Voir la simulation
                    </Button>
                  </td>
                </tr>
              ))}
              {leads.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">Aucun client pour le moment.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
